import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authAPI from '../services/authService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState(() => {
    const savedTokens = localStorage.getItem('tokens');
    return savedTokens ? JSON.parse(savedTokens) : null;
  });

  // Load user data on mount if tokens exist
  useEffect(() => {
    const loadUser = async () => {
      if (tokens?.access_token) {
        try {
          const userData = await authAPI.getCurrentUser(tokens.access_token);
          setUser(userData);
        } catch (error) {
          console.error('Failed to load user:', error);
          // Token might be expired, try to refresh
          if (tokens?.refresh_token) {
            try {
              const newTokens = await authAPI.refreshToken(tokens.refresh_token);
              setTokens(newTokens);
              localStorage.setItem('tokens', JSON.stringify(newTokens));
              const userData = await authAPI.getCurrentUser(newTokens.access_token);
              setUser(userData);
            } catch (refreshError) {
              // Refresh failed, clear tokens
              logout();
            }
          } else {
            logout();
          }
        }
      }
      setLoading(false);
    };

    loadUser();
  }, []);

  const login = (newTokens, userData) => {
    setTokens(newTokens);
    setUser(userData);
    localStorage.setItem('tokens', JSON.stringify(newTokens));
  };

  const logout = async () => {
    if (tokens?.access_token) {
      try {
        await authAPI.logout(tokens.access_token);
      } catch (error) {
        console.error('Logout error:', error);
      }
    }
    setTokens(null);
    setUser(null);
    localStorage.removeItem('tokens');
  };

  const updateUser = (userData) => {
    setUser(userData);
  };

  const value = {
    user,
    tokens,
    loading,
    login,
    logout,
    updateUser,
    isAuthenticated: !!user && !!tokens,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
